/* eslint-disable react/forbid-prop-types */
import { CustomSelect, Label } from '@4beefopag/4bee-fopag-core/dist/atoms';
import { Select, Skeleton } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { MODULE_BENEFIT_ORDER, MODULE_PAYROLL_ORDER } from '../../../constants/modules';
import * as CompanyRepository from '../../../services/repository/companyRepository';
import MultiSelect from '../../molecules/MultiSelect';
import { normalizeString } from '../../../services/util/string';

const { Option } = Select;

function SelectFolha({
  folhaSelecionada = undefined,
  idFolhaSelecionada = undefined,
  shouldAutoSelectSingleCompany = false,
  onCompaniesLoaded = undefined,
  disabled = false,
  onChange,
  folhas: folhasParam = undefined,
  module = undefined,
  placeholder = undefined,
  showLabel = true,
  isMulti = false,
}) {
  const criteria = useMemo(() => (module ? { modules: [module] } : {}), [module]);
  const { isPending, data: fetchedData } = CompanyRepository.useAccessibleCompanies(criteria);
  const hasExplicitFolhas = Array.isArray(folhasParam);

  const folhas = useMemo(() => {
    if (hasExplicitFolhas) return folhasParam;
    return fetchedData || [];
  }, [hasExplicitFolhas, folhasParam, fetchedData]);

  const isLoadingOptions = !hasExplicitFolhas && isPending;
  const hasLoadedFolhas = !isLoadingOptions && folhas.length > 0;

  useEffect(() => {
    if (hasLoadedFolhas && onCompaniesLoaded) {
      onCompaniesLoaded(folhas);
    }
  }, [hasLoadedFolhas, onCompaniesLoaded, folhas]);

  useEffect(() => {
    const isSingleItem = folhas.length === 1;
    const canAutoSelect = hasLoadedFolhas && !isMulti && isSingleItem && shouldAutoSelectSingleCompany;

    if (canAutoSelect) {
      onChange(folhas[0]);
    }
  }, [hasLoadedFolhas, isMulti, shouldAutoSelectSingleCompany, folhas]);

  useEffect(() => {
    const mustSyncById = idFolhaSelecionada && !folhaSelecionada;

    if (!hasLoadedFolhas || !mustSyncById) return;

    const matchedFolha = folhas.find((folha) => folha.idFolha === idFolhaSelecionada);
    if (matchedFolha) {
      onChange(matchedFolha);
    }
  }, [hasLoadedFolhas, idFolhaSelecionada, folhaSelecionada, folhas]);

  const selectedIds = useMemo(() => {
    if (!isMulti || !Array.isArray(folhaSelecionada)) return [];
    return folhaSelecionada.map((f) => f.idFolha);
  }, [isMulti, folhaSelecionada]);

  const areAllSelected = isMulti && selectedIds.length === folhas.length && folhas.length > 0;

  const handleMultiChange = (ids) => {
    const selected = ids.map((id) => folhas.find((f) => f.idFolha === id)).filter(Boolean);
    onChange(selected);
  };

  const sortFolhasByDescricao = () =>
    [...folhas].sort((a, b) => (a.descricao ?? '').localeCompare(b.descricao ?? '', 'pt-BR', { sensitivity: 'base' }));

  const renderOptions = () =>
    sortFolhasByDescricao().map((item) => (
      <Option
        key={item.idFolha}
        value={item.idFolha}
      >
        {`<${item.idFolha}> ${item.descricao}`}
      </Option>
    ));

  const renderMultiSelect = () => (
    <MultiSelect
      placeholder={placeholder || 'Selecione...'}
      loading={isLoadingOptions}
      disabled={disabled}
      value={selectedIds}
      onChange={(ids) => handleMultiChange(ids)}
      areAllSelected={areAllSelected}
      onChangeAllSelected={(checked) => onChange(checked ? [...folhas] : [])}
      // Espaço não separa tokens, permitindo buscar por descrição completa
      tokenSeparators={[',']}
      // Mantém o termo buscado após selecionar, para seleção múltipla na lista filtrada
      autoClearSearchValue={false}
      ignoreAccents
    >
      {renderOptions()}
    </MultiSelect>
  );

  const renderSingleSelect = () => (
    <CustomSelect
      showSearch
      optionFilterProp="children"
      placeholder={placeholder || 'Selecione uma folha...'}
      loading={isLoadingOptions}
      allowClear
      value={(folhaSelecionada && folhaSelecionada.idFolha) || idFolhaSelecionada}
      disabled={disabled}
      onChange={(id) => onChange(folhas.find(({ idFolha }) => idFolha === id))}
      filterOption={(input, option) => normalizeString(option.props.children).indexOf(normalizeString(input)) > -1}
      notFoundContent={isLoadingOptions ? <Skeleton /> : null}
    >
      {renderOptions()}
    </CustomSelect>
  );

  const dropdown = isMulti ? renderMultiSelect() : renderSingleSelect();

  if (!showLabel) return dropdown;

  return <Label label="Folha">{dropdown}</Label>;
}

SelectFolha.propTypes = {
  folhaSelecionada: PropTypes.oneOfType([PropTypes.shape(), PropTypes.arrayOf(PropTypes.shape())]),
  idFolhaSelecionada: PropTypes.number,
  shouldAutoSelectSingleCompany: PropTypes.bool,
  onCompaniesLoaded: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  folhas: PropTypes.arrayOf(PropTypes.shape()),
  module: PropTypes.oneOf([MODULE_PAYROLL_ORDER, MODULE_BENEFIT_ORDER]),
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  showLabel: PropTypes.bool,
  isMulti: PropTypes.bool,
};

export default SelectFolha;
